import { useEffect, useRef, useState, type ReactNode } from "react";

type Direction = "left" | "right" | "up";

type Props = {
  children: ReactNode;
  direction?: Direction;
  delay?: number;
  className?: string;
};

export function Reveal({ children, direction = "up", delay = 0, className }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const hidden =
    direction === "left"
      ? "opacity-0 -translate-x-10 md:-translate-x-16"
      : direction === "right"
        ? "opacity-0 translate-x-10 md:translate-x-16"
        : "opacity-0 translate-y-8 md:translate-y-10";

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-[900ms] md:duration-[1100ms] ease-out ${
        visible ? "opacity-100 translate-x-0 translate-y-0" : hidden
      } ${className ?? ""}`}
    >
      {children}
    </div>
  );
}

export default Reveal;
